// Auto-recover from stale lazy-chunk loads after a deploy.
//
// Problem (hit twice in the 0.62.x pilot): a user keeps the SPA open across a
// deploy. The browser still holds the old index.html referencing old chunk
// hashes; after the deploy those chunk files are gone. Clicking a lazy-loaded
// route (e.g. Platform Management) then throws ChunkLoadError and the route
// silently dies — the user sees a dead page until a manual hard-reload.
//
// Fix: detect that specific error and reload ONCE automatically (a fresh
// index.html then references the current chunks). A sessionStorage cooldown
// guards against a reload loop if the chunk is genuinely missing for another
// reason.
import { ErrorHandler, Injectable } from '@angular/core';

const RELOAD_GUARD_KEY = 'fap.chunkReloadAt';
const RELOAD_COOLDOWN_MS = 10_000;

/** True for the "stale chunk after deploy" family of errors (webpack + native ESM). */
export function isChunkLoadError(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null;
  if (!e) return false;
  if (e.name === 'ChunkLoadError') return true;
  return /Loading chunk [\w./-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
    e.message ?? '',
  );
}

/** Reload only if we haven't just reloaded (loop guard). Pure — unit-tested. */
export function shouldReloadForChunkError(now: number, lastReloadAt: number | null): boolean {
  if (lastReloadAt === null) return true;
  return now - lastReloadAt > RELOAD_COOLDOWN_MS;
}

/** Attempt the one-shot reload; returns true if a reload was triggered. */
export function recoverFromChunkError(err: unknown): boolean {
  if (!isChunkLoadError(err)) return false;
  let last: number | null = null;
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY);
    last = raw ? Number(raw) : null;
  } catch {
    last = null; // private mode / storage blocked → treat as first time
  }
  const now = Date.now();
  if (!shouldReloadForChunkError(now, last)) return false;
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    /* ignore */
  }
  window.location.reload();
  return true;
}

/** Global handler — catches ChunkLoadError from dynamic imports outside the router. */
@Injectable()
export class ChunkErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    if (recoverFromChunkError(error)) return;
    console.error(error);
  }
}
